import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Option, Result } from '@sapphire/result';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { excelToJson, Nullable } from '@ps/utils';
import { ToastService } from '@ps/core-overlay';
import { SurveyService } from '@ps/survey-data-access';
import {
  RESPONDENT_LIST_ORIGIN,
  respondentListOriginOf,
  UniqueLinkPsidUploadResult,
  validateUniqueLinkPsidRows,
} from '@ps/survey-utils';

import { RespondentListService } from '../../services/respondent-list/respondent-list.service';
import { UniqueBuyerEntryLinksService } from '../../services/unique-buyer-entry-links/unique-buyer-entry-links-service';

import {
  selectSurvey,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';

/**
 * Upload/download/view/clear for the Include/Exclude respondent list, plus
 * its activity logging. Split from `SurveyCreationLaunchEffects`
 */
@Injectable()
export class SurveyCreationRespondentListEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _surveyService = inject(SurveyService);
  private readonly _respondentListService = inject(RespondentListService);
  private readonly _uniqueBuyerEntryLinksService = inject(
    UniqueBuyerEntryLinksService,
  );
  private readonly _toastService = inject(ToastService);

  public readonly downloadRespondentListTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationLaunchActions.downloadRespondentListTemplate),
        switchMap(() =>
          this._surveyService
            .downloadRespondentListTemplate()
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  // Rehydrates the respondent-list View drawer for a survey that already has an uploaded list
  public readonly loadRespondentListOnSurveyLoaded$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        // Legacy: the PSID list is re-fetched on a Traditional MC locale switch.
        SurveyCreationCoreApiActions.localeSurveyRefreshed,
      ),
      filter(({ survey }) => !!survey.hasPsidRefList),
      map(({ survey }) =>
        SurveyCreationLaunchActions.loadRespondentList({
          inclExcl: survey.inclExcl === 1 ? 1 : 0,
        }),
      ),
    ),
  );

  public readonly uploadRespondentList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.uploadRespondentList),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      mergeMap(([{ file }, surveyId]) =>
        this._respondentListService
          .upload(Option.from(surveyId).unwrap(), file)
          .pipe(
            tap(() =>
              this._toastService.success(RESPONDENT_LIST_UPLOADED_MESSAGE, {
                keepOpen: false,
              }),
            ),
            map((items) =>
              SurveyCreationLaunchApiActions.uploadRespondentListSuccess({
                items,
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationLaunchApiActions.uploadRespondentListFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly downloadUniqueLinkPsidTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationLaunchActions.downloadUniqueLinkPsidTemplate),
        switchMap(() =>
          this._uniqueBuyerEntryLinksService
            .downloadUniqueLinkPsidTemplateFile()
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  // The file is checked in the browser first (2 columns: PSID + link), then
  // parsed by the server; the rows are only stored when the survey is saved.
  public readonly uploadUniqueLinkPsidFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.uploadUniqueLinkPsidFile),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(hasSurveyId),
      mergeMap(([{ file }, surveyId]) =>
        this._checkedUniqueLinkPsidFile(file).pipe(
          switchMap((checked) =>
            checked.match({
              ok: () => this._uploadUniqueLinkPsidFile(surveyId, file),
              err: (error) => this._rejectUniqueLinkPsidFile(error),
            }),
          ),
        ),
      ),
    ),
  );

  public readonly loadRespondentList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.loadRespondentList),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurvey),
      ]),
      mergeMap(([{ inclExcl }, surveyId, survey]) =>
        this._respondentListService
          .getUploadedList(surveyId ?? 0, inclExcl)
          .pipe(
            map((items) =>
              SurveyCreationLaunchApiActions.loadRespondentListSuccess({
                items,
                origin: Option.from(survey).mapOr(
                  RESPONDENT_LIST_ORIGIN.BUYER_UPLOAD,
                  respondentListOriginOf,
                ),
              }),
            ),
            this.psCatchError((error) =>
              of(
                SurveyCreationLaunchApiActions.loadRespondentListFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public readonly clearRespondentList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.clearRespondentList),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      mergeMap(([, surveyId]) =>
        this._respondentListService.clear(Option.from(surveyId).unwrap()).pipe(
          tap(() =>
            this._toastService.success(RESPONDENT_LIST_CLEARED_MESSAGE, {
              keepOpen: false,
            }),
          ),
          map(() =>
            SurveyCreationLaunchApiActions.clearRespondentListSuccess(),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationLaunchApiActions.clearRespondentListFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly logRespondentListUpload$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchApiActions.uploadRespondentListSuccess),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      map(([, surveyId]) =>
        SurveyCreationLaunchActions.logSurveyLaunchActivity({
          surveyId: Option.from(surveyId).unwrap(),
          trigger: { kind: 'includeExcludeListUploaded' },
        }),
      ),
    ),
  );

  // Funnels through the same logSurveyLaunchActivity$ pipeline as the
  // launchFeatures toggles — clearing always represents an ON -> OFF transition.
  public readonly logRespondentListClear$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchApiActions.clearRespondentListSuccess),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      map(([, surveyId]) =>
        SurveyCreationLaunchActions.logSurveyLaunchActivity({
          surveyId: Option.from(surveyId).unwrap(),
          trigger: {
            kind: 'launchToggle',
            field: 'includeExcludeListCleared',
            enabled: false,
          },
        }),
      ),
    ),
  );

  public readonly logIncludeExcludeStatusToggled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.toggleIncludeExcludeStatus),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      map(([{ enabled }, surveyId]) =>
        SurveyCreationLaunchActions.logSurveyLaunchActivity({
          surveyId: Option.from(surveyId).unwrap(),
          trigger: {
            kind: 'launchToggle',
            field: 'includeExcludeRespondentsEnabled',
            enabled,
          },
        }),
      ),
    ),
  );

  private _checkedUniqueLinkPsidFile(
    file: File,
  ): Observable<Result<void, string>> {
    return excelToJson(file).pipe(
      map((rows) => validateUniqueLinkPsidRows(rows)),
      // excelToJson rejects with a string for its own checks (extension,
      // size), but a corrupt or mislabelled file fails inside xlsx itself.
      catchError((error: unknown) =>
        of(
          Result.err(
            typeof error === 'string' ? error : UNREADABLE_FILE_MESSAGE,
          ),
        ),
      ),
    );
  }

  private _uploadUniqueLinkPsidFile(
    surveyId: number,
    file: File,
  ): Observable<Action> {
    return this._respondentListService
      .uploadUniqueLinkPsidFile(surveyId, file)
      .pipe(
        tap((result) =>
          result.links.length
            ? this._toastService.success(uploadSummaryOf(result), {
                keepOpen: false,
              })
            : this._toastService.error(UNIQUE_LINK_PSID_FILE_INVALID_MESSAGE, {
                keepOpen: false,
              }),
        ),
        map((result) =>
          result.links.length
            ? SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileSuccess({
                result,
              })
            : SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileFailure({
                error: UNIQUE_LINK_PSID_FILE_INVALID_MESSAGE,
              }),
        ),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  private _rejectUniqueLinkPsidFile(error: string): Observable<Action> {
    this._toastService.error(error, { keepOpen: false });
    return of(
      SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileFailure({ error }),
    );
  }
}

// Narrows the survey id to a number, so no Option unwrap is needed downstream.
function hasSurveyId<TAction>(
  pair: [TAction, Nullable<number>],
): pair is [TAction, number] {
  return !!pair[1];
}

// Legacy wording for the PSID-only respondent list.
const RESPONDENT_LIST_UPLOADED_MESSAGE = 'File added successfully';
const RESPONDENT_LIST_CLEARED_MESSAGE = 'Success! PSIDs have been cleared';

const UNREADABLE_FILE_MESSAGE =
  'The file could not be read. Please upload a .csv, .xls or .xlsx file based on the Unique Entry Link & PSID template.';

const UNIQUE_LINK_PSID_FILE_INVALID_MESSAGE =
  'PSID file is empty / invalid. Please update to continue';

// Legacy recontact wording.
function uploadSummaryOf({
  links,
  uploadedCount,
  skippedCount,
}: UniqueLinkPsidUploadResult): string {
  const summary = `${links.length} out of ${uploadedCount} psid successfully uploaded.`;
  return skippedCount ? `${summary} ${skippedCount} psid skipped` : summary;
}
